
// import * as React from "react"
// import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { COLOR_ETIQUETA_POR_DEFECTO } from "@/lib/colores-de-etiqueta"

export interface TagBadgeProps {
  id: string
  name: string
  color?: string
  // type?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'outline' | 'secondary' | 'soft'
  removable?: boolean
  onRemove?: (id: string) => void
  className?: string
  onClick?: () => void
}

export function TagBadge({
  id,
  name,
  color = COLOR_ETIQUETA_POR_DEFECTO,
  // type,
  size = "md",
  variant = "default",
  removable = false,
  onRemove,
  className,
  onClick,
  ...props
}: TagBadgeProps) {
  const sizeClasses = {
    sm: "text-xs px-2 py-1 h-5",
    md: "text-sm px-2.5 py-1 h-6",
    lg: "text-sm px-3 py-1.5 h-7"
  }

  const variantClasses = {
    default: "text-white border-transparent",
    outline: "bg-transparent border-current",
    secondary: "bg-secondary text-secondary-foreground border-transparent",
    // El color de la etiqueta, atenuado: se lee, pero no grita. Es como se
    // ensena una etiqueta que se SUGIERE, todavia sin elegir. Estaba escrito a
    // mano en el constructor de filtros, con sus tres opacidades en hex.
    soft: "border font-medium"
  }

  // Convert hex color to RGB for alpha variations
  // const hexToRgb = (hex: string) => {
  //   const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  //   return result ? {
  //     r: parseInt(result[1], 16),
  //     g: parseInt(result[2], 16),
  //     b: parseInt(result[3], 16)
  //   } : null
  // }

  // const rgb = hexToRgb(color)
  const bgColor = variant === 'default' ? color : 'transparent'
  const borderColor = variant === 'outline' ? color : 'transparent'
  const backgroundColor = variant === 'secondary' ? undefined : bgColor

  const style =
    variant === 'secondary'
      ? undefined
      : variant === 'soft'
        ? { backgroundColor: `${color}20`, borderColor: `${color}30`, color }
        : {
            backgroundColor,
            borderColor,
            color: variant === 'outline' ? color : undefined,
          }

  // Si se pulsa, es un boton: asi responde al teclado y se anuncia como lo que
  // es. Aqui el elemento crudo es lo correcto —una etiqueta lleva SU color, no
  // el de una variante de Button—, y por eso vive en components/ui.
  const Elemento = onClick ? 'button' : 'div'

  return (
    <Elemento
      type={onClick ? 'button' : undefined}
      className={cn(
        "inline-flex items-center rounded-full border font-medium transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        sizeClasses[size],
        variantClasses[variant],
        onClick && "cursor-pointer hover:opacity-80",
        className
      )}
      style={style}
      onClick={onClick}
      {...props}
    >
      <span className="truncate">{name}</span>
      {removable && onRemove && (
        <button
          type="button"
          aria-label="Quitar"
          className={cn(
            "ml-1 flex h-4 w-4 items-center justify-center rounded-full hover:bg-accent hover:text-accent-foreground",
            variant === 'outline' && "hover:bg-current/10"
          )}
          onClick={(e) => {
            e.stopPropagation()
            onRemove(id)
          }}
        >
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span className="sr-only">Remove tag</span>
        </button>
      )}
    </Elemento>
  )
}

export interface TagListProps {
  tags: Array<{
    id: string
    name: string
    color?: string
    type?: string
  }>
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'outline' | 'secondary' | 'soft'
  removable?: boolean
  onRemove?: (id: string) => void
  onTagClick?: (tag: { id: string; name: string }) => void
  className?: string
  maxTags?: number
  showCount?: boolean
}

export function TagList({
  tags,
  size = "md",
  variant = "default",
  removable = false,
  onRemove,
  onTagClick,
  className,
  maxTags,
  showCount = false,
  ...props
}: TagListProps) {
  const displayTags = maxTags ? tags.slice(0, maxTags) : tags
  const remainingCount = maxTags && tags.length > maxTags ? tags.length - maxTags : 0

  if (tags.length === 0) {
    return (
      <div className={cn("text-sm text-muted-foreground", className)}>
        No tags assigned
      </div>
    )
  }

  return (
    <div className={cn("flex flex-wrap gap-1", className)} {...props}>
      {displayTags.map((tag) => (
        <TagBadge
          key={tag.id}
          id={tag.id}
          name={tag.name}
          color={tag.color}
          // type={tag.type}
          size={size}
          variant={variant}
          removable={removable}
          onRemove={onRemove}
          onClick={onTagClick ? () => onTagClick(tag) : undefined}
        />
      ))}
      {remainingCount > 0 && (
        <div className={cn(
          "inline-flex items-center rounded-full border bg-muted text-muted-foreground",
          sizeClasses[size]
        )}>
          +{remainingCount} more
        </div>
      )}
      {showCount && (
        <div className={cn(
          "inline-flex items-center rounded-full border bg-secondary text-secondary-foreground",
          sizeClasses[size]
        )}>
          {tags.length} tag{tags.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}

const sizeClasses = {
  sm: "text-xs px-2 py-1 h-5",
  md: "text-sm px-2.5 py-1 h-6",
  lg: "text-sm px-3 py-1.5 h-7"
}